import type { RouterLinkProps } from "@/components/Action.tsx";
import { configAtom } from "@/config.ts";
import type { AccessDelegationWithType } from "@/modules/access/delegation/accessDelegationApi.ts";
import type { AccessImpersonationWithType } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { delegationLink, tokenLink } from "@/modules/access/links.ts";
import type { ApiTokenWithType } from "@/modules/access/token/apiTokenApi.ts";
import type { BillingAccountWithType } from "@/modules/billing/billingAccountQuery.ts";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { vmLink } from "@/modules/compute/links.ts";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import type { DiskWithPrice } from "@/modules/disks/disksListQuery.ts";
import { diskLink } from "@/modules/disks/links.ts";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { leaseLink } from "@/modules/metal/links.ts";
import type { MetalListItemWithType } from "@/modules/metal/metalQuery.ts";
import type { FirewallWithType } from "@/modules/network/firewall/firewallApi.ts";
import type { IpAddressWithType } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { firewallLink, ipAddressLink, loadBalancerLink, vpcLink } from "@/modules/network/links.ts";
import type { LoadBalancerWithType } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import type { VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { serviceLink } from "@/modules/services/links.ts";
import type { ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import type { StorageAccountWithType } from "@/modules/storage/account/storageAccountApi.ts";
import type { BucketWithType } from "@/modules/storage/bucket/bucketApi";
import { bucketLink, storageAccountLink } from "@/modules/storage/links.ts";
import { profileLink } from "@/modules/user/profileLink.ts";
import { Trans } from "@lingui/react/macro";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { atomFamily } from "jotai-family";
import { atom } from "jotai/vanilla";
import type { ReactNode } from "react";

//#region Type definitions

/**
 * Resources that can be created by the user and are returned from the API (and can be shown eg. in the Dashboard
 * "Recently modified" view).
 *
 * NB: Only add that kind of resources here. For other kinds, just create a different, new `type` and constants.
 *
 * NB: Dashboard will show them in the order they are defined here.
 */
export const resourceTypes = [
    "virtual_machine",
    "bare_metal",
    "storage_account",
    "bucket",
    "ip_address",
    "vpc",
    "load_balancer",
    "access_delegation",
    "api_token",
    "access_impersonation",
    "billing_account",
    "managed_service",
    "firewall",
    "disk",
] as const;

export type ResourceType = (typeof resourceTypes)[number];
//#endregion

//#region Extra information
export type ResourceTypeOrDefault = ResourceType | "_default";

// Not translated since it should never be shown to the user
const missing = "--- Missing ---";

const ResourceIconClasses: Record<ResourceTypeOrDefault, string> = {
    _default: "jp-icon-appcatalog",
    virtual_machine: "jp-virtual-machine-icon",
    load_balancer: "jp-load-balancer-icon",
    vpc: "jp-network-icon",
    api_token: "jp-swap-icon",
    access_delegation: "jp-user-group-icon",
    access_impersonation: "jp-user-group-icon",
    bucket: "jp-stack-icon",
    ip_address: "jp-network-2-icon",
    billing_account: "jp-card-icon",
    managed_service: "jp-services-icon",
    bare_metal: "jp-host-icon",
    firewall: "i-lucide:shield-ban",
    disk: "jp-ssd-disk-icon",
    storage_account: "jp-archive-icon",
};

const ResourceNames: Record<ResourceTypeOrDefault, Translatable> = {
    _default: missing,
    virtual_machine: <Trans>Virtual machine</Trans>,
    load_balancer: <Trans>Load balancer</Trans>,
    vpc: <Trans>VPC network</Trans>,
    api_token: <Trans>API token</Trans>,
    access_delegation: <Trans>Access delegation</Trans>,
    access_impersonation: <Trans>Access invitation</Trans>,
    bucket: <Trans>Storage bucket</Trans>,
    ip_address: <Trans>Floating IP</Trans>,
    billing_account: <Trans>Billing account</Trans>,
    managed_service: <Trans>Managed service</Trans>,
    bare_metal: <Trans>Bare metal server</Trans>,
    firewall: <Trans>Firewall</Trans>,
    disk: <Trans>Disk</Trans>,
    storage_account: <Trans>Storage account</Trans>,
};

const compute: Translatable = <Trans>Compute</Trans>;
const network: Translatable = <Trans>Network</Trans>;
const access: Translatable = <Trans>Access</Trans>;
const storage: Translatable = <Trans>Storage</Trans>;
const billing: Translatable = <Trans>Billing</Trans>;
const services: Translatable = <Trans>Services</Trans>;
const disks: Translatable = <Trans>Disks</Trans>;

const ResourceParent: Record<ResourceTypeOrDefault, Translatable> = {
    _default: missing,
    // TODO: Move labels to common constants shared with `src/modules/main/sidebar/Sidebar.sections.tsx` so they do not get out of sync
    virtual_machine: compute,
    load_balancer: network,
    vpc: network,
    api_token: access,
    access_delegation: access,
    access_impersonation: access,
    bucket: storage,
    ip_address: network,
    billing_account: billing,
    managed_service: services,
    bare_metal: compute,
    firewall: network,
    disk: disks,
    storage_account: storage,
};

// NOTE: These might be shared between eg. the Dashboard, and the Create views (for the description next to the type selector)
const storageBucketDescription = (
    <Trans>
        Create a new storage account, create some buckets and start storing your data in a scalable way -
        Pay-as-you-grow.
    </Trans>
);

const ResourceDescription: Record<ResourceTypeOrDefault, ReactNode | undefined> = {
    _default: missing,
    virtual_machine: (
        <Trans>
            Create a new Virtual Machine or choose something from the App Catalog to get your services running in
            minutes.
        </Trans>
    ),
    load_balancer: (
        <Trans>
            High availability load balancer distributes traffic between virtual machines within the same data center
            location.
        </Trans>
    ),
    vpc: <Trans>Create a personal Virtual Private Cloud (VPC) and start managing your networks.</Trans>,
    api_token: <Trans>Create an API Token to use our API.</Trans>,
    access_delegation: (
        <Trans>Delegate access to your user or billing account so trusted users can manage your resources.</Trans>
    ),
    access_impersonation: "",
    bucket: storageBucketDescription,
    ip_address: <Trans>Allocate a floating IP and start managing your network.</Trans>,
    billing_account: undefined,
    managed_service: (
        <Trans>
            Fully managed services - we handle the provisioning of underlying infrastructure and the management of the
            service.
        </Trans>
    ),
    bare_metal: <Trans>Create a dedicated bare metal server and run your application with maximum performance.</Trans>,
    firewall: (
        <Trans>
            With a firewall you can control incoming and outgoing network traffic based on predetermined security rules.
        </Trans>
    ),
    // TODO: Add description
    disk: "",
    storage_account: storageBucketDescription,
};

export function getResourceTypeName(type: ResourceType) {
    return ResourceNames[type] ?? ResourceNames._default;
}

export function getResourceParent(type: ResourceType) {
    return ResourceParent[type] ?? ResourceParent._default;
}

export function getResourceDescription(type: ResourceType) {
    return ResourceDescription[type] ?? ResourceDescription._default;
}

export function getResourceIconClass(type: ResourceType) {
    return ResourceIconClasses[type] ?? ResourceIconClasses._default;
}

//#endregion

// TODO: bucket, service, invoice ...
export type AnyResource =
    | VirtualMachineLoc
    | ManagedServiceLoc
    | VpcWithType
    | LoadBalancerWithType
    | IpAddressWithType
    | AccessDelegationWithType
    | ApiTokenWithType
    | BucketWithType
    | AccessImpersonationWithType
    | BillingAccountWithType
    | MetalListItemWithType
    | FirewallWithType
    | DiskWithPrice
    | StorageAccountWithType;

export function getResourceLink(item: AnyResource): RouterLinkProps {
    const { $type } = item;
    switch ($type) {
        case "virtual_machine":
            return vmLink(item);
        case "managed_service":
            return serviceLink(item);
        case "bucket":
            return bucketLink(item);
        case "load_balancer":
            return loadBalancerLink(item);
        case "firewall":
            return firewallLink(item);
        case "disk":
            return diskLink(item);
        case "vpc":
            return vpcLink(item);
        case "ip_address":
            return ipAddressLink(item);
        case "access_delegation":
            return delegationLink(item);
        case "api_token":
            return tokenLink(item);
        case "billing_account":
            return billingAccountLink(item);
        case "access_impersonation":
            return profileLink;
        case "bare_metal":
            return leaseLink(item);
        case "storage_account":
            return storageAccountLink(item);
        default:
            exhaustiveSwitchCheck($type);
    }
}

export const resourceTypesEnabledAtom = atom((get): Record<ResourceType, boolean> => {
    const config = get(configAtom);
    return {
        virtual_machine: config.vmsEnabled,
        managed_service: config.servicesEnabled,
        bucket: config.storageEnabled,
        load_balancer: config.loadBalancerEnabled,
        vpc: config.privateNetworksEnabled,
        ip_address: config.ipsEnabled,
        bare_metal: config.bareMetalEnabled,
        firewall: config.firewallEnabled,
        disk: config.disksEnabled,
        access_delegation: true,
        api_token: true,
        access_impersonation: true,
        billing_account: true,
        storage_account: config.storageEnabled,
    };
});

export const resourceTypeEnabledAtom = atomFamily((type: ResourceType) =>
    atom((get) => get(resourceTypesEnabledAtom)[type]),
);

/** Simple utility atom factory to check if any {@link ResourceType} is enabled */
export function anyResourceEnabledAtomFactory(types: ResourceType[]) {
    return atom((get) => {
        const enabled = get(resourceTypesEnabledAtom);
        return types.some((type) => enabled[type]);
    });
}
